import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType, concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { merge, of } from 'rxjs';
import { debounceTime, filter, switchMap, exhaustMap, map, catchError, tap } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { MatSnackBar } from '@angular/material/snack-bar';
import { mappedHttpErrorResponseOperator } from '@adnova/jf-ng-components';

import { AnnouncementAdminService, AnnouncementService, ProblemDTO } from '../../../openapi/shell-service-openapi';
import { AdminAnnouncementActions } from './admin-announcement.actions';
import { AdminAnnouncementSelectors } from './admin-announcement.selectors';
import {
  AnnouncementEntitiesActions,
} from '../../../store/announcement-entities';
import {
  AnnouncementDetailEntitiesActions,
} from '../../../store/announcement-detail-entities';
import {
  DeleteAnnouncementDialogActions,
  DeleteAnnouncementDialogSelectors,
} from '../../../modules/dialogs/delete-announcement-dialog';
import {
  InfoPopupDialogActions,
} from '../../../modules/dialogs/info-popup-dialog';
import { AnnouncementPreviewData, getErrorMessage, SNACKBAR_MESSAGES } from '../+definitions';
import { AnnouncementImageStagingService } from '../announcement-image-staging.service';

/**
 * Effects für das Admin-Announcement Dashboard.
 */
@Injectable()
export class AdminAnnouncementEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly service = inject(AnnouncementAdminService);
  private readonly announcementService = inject(AnnouncementService);
  private readonly logger = inject(NGXLogger);
  private readonly imageStagingService = inject(AnnouncementImageStagingService);
  private readonly snackBar = inject(MatSnackBar);

  /**
   * Initialisiert das Dashboard.
   * Lädt Announcements und Statistiken beim ersten Aufruf.
   */
  readonly init$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAnnouncementActions.init),
      concatLatestFrom(() => this.store.select(AdminAnnouncementSelectors.pageable)),
      switchMap(([_, pageable]) => [
        AnnouncementEntitiesActions.loadList({ pageable }),
        AnnouncementEntitiesActions.loadStatistics(),
      ]),
    ),
  );

  /**
   * Reagiert auf Pagination-Änderungen.
   * Lädt die Liste mit den neuen Parametern neu.
   */
  readonly setPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAnnouncementActions.setPage),
      concatLatestFrom(() => this.store.select(AdminAnnouncementSelectors.pageable)),
      switchMap(([_, pageable]) => [
        AnnouncementEntitiesActions.loadList({ pageable }),
      ]),
    ),
  );

  /**
   * Reagiert auf Sortier-Änderungen.
   * Lädt die Liste mit den neuen Parametern neu (pageIndex wird im Reducer auf 0 gesetzt).
   */
  readonly setSort$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAnnouncementActions.setSort),
      concatLatestFrom(() => this.store.select(AdminAnnouncementSelectors.pageable)),
      switchMap(([_, pageable]) => [
        AnnouncementEntitiesActions.loadList({ pageable }),
      ]),
    ),
  );

  /**
   * Reagiert auf erfolgreiche Mutationen (Delete, Publish, Unpublish, Save).
   * Lädt Liste und Statistiken neu.
   */
  readonly reloadAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AnnouncementEntitiesActions.deleteSuccess,
        AnnouncementEntitiesActions.publishSuccess,
        AnnouncementEntitiesActions.unpublishSuccess,
        AdminAnnouncementActions.saveSuccess,
      ),
      concatLatestFrom(() => this.store.select(AdminAnnouncementSelectors.pageable)),
      switchMap(([_, pageable]) => [
        AnnouncementEntitiesActions.loadList({ pageable }),
        AnnouncementEntitiesActions.loadStatistics(),
      ]),
    ),
  );

  /**
   * Reagiert auf Bestätigung im Delete-Dialog.
   * Dispatcht die eigentliche Delete-Action und schließt den Dialog.
   */
  readonly confirmDelete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteAnnouncementDialogActions.confirm),
      concatLatestFrom(() => this.store.select(DeleteAnnouncementDialogSelectors.itemToDelete)),
      filter(([_, item]) => !!item),
      switchMap(([_, item]) => [
        AnnouncementEntitiesActions.delete({ id: item!.id }),
        DeleteAnnouncementDialogActions.close(),
      ]),
    ),
  );

  /**
   * Öffnet den Editor im Bearbeiten-Modus.
   * Dispatcht loadDetail an den Detail-Entity-Store.
   */
  readonly openEdit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAnnouncementActions.openEdit),
      map(({ id }) => AnnouncementDetailEntitiesActions.loadDetail({ id })),
    ),
  );

  /**
   * Räumt Detail-Daten auf und schließt den Vorschau-Dialog, wenn der Editor geschlossen wird.
   */
  readonly closeEditor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAnnouncementActions.closeEditor),
      switchMap(() => [
        AnnouncementDetailEntitiesActions.clearAll(),
        InfoPopupDialogActions.close(),
      ]),
    ),
  );

  /**
   * Speichert ein neues Announcement als Entwurf.
   * Erstellt das Announcement und lädt optional ein Bild hoch.
   */
  readonly saveAsDraft$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAnnouncementActions.saveAsDraft),
      exhaustMap(({ dto }) => {
        const { imageFile } = this.imageStagingService.consume();
        return this.service.createAnnouncement(dto).pipe(
          switchMap((response) => {
            if (imageFile) {
              return this.service.uploadAnnouncementImage(response.id, imageFile).pipe(
                map(() => AdminAnnouncementActions.saveSuccess()),
              );
            }
            return of(AdminAnnouncementActions.saveSuccess());
          }),
          catchError((error) => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map((mappedError) => {
              this.logger.error('Fehler beim Speichern als Entwurf.', mappedError);
              return AdminAnnouncementActions.saveFailure({ error: mappedError });
            }),
          )),
        );
      }),
    ),
  );

  /**
   * Speichert ein neues Announcement und veröffentlicht es direkt.
   * Erstellt das Announcement, lädt optional ein Bild hoch und veröffentlicht es.
   */
  readonly saveAndPublish$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAnnouncementActions.saveAndPublish),
      exhaustMap(({ dto }) => {
        const { imageFile } = this.imageStagingService.consume();
        return this.service.createAnnouncement(dto).pipe(
          switchMap((response) => {
            if (imageFile) {
              return this.service.uploadAnnouncementImage(response.id, imageFile).pipe(
                switchMap(() => this.service.publishAnnouncement(response.id)),
              );
            }
            return this.service.publishAnnouncement(response.id);
          }),
          map(() => AdminAnnouncementActions.saveSuccess()),
          catchError((error) => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map((mappedError) => {
              this.logger.error('Fehler beim Speichern und Veröffentlichen.', mappedError);
              return AdminAnnouncementActions.saveFailure({ error: mappedError });
            }),
          )),
        );
      }),
    ),
  );

  /**
   * Aktualisiert ein bestehendes Announcement.
   * Aktualisiert die Daten und handhabt optional Bild-Upload oder Bild-Löschung.
   */
  readonly updateAnnouncement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAnnouncementActions.updateAnnouncement),
      exhaustMap(({ id, dto }) => {
        const { imageFile, imageDeleted } = this.imageStagingService.consume();
        return this.service.updateAnnouncement(id, dto).pipe(
          switchMap(() => {
            if (imageFile) {
              return this.service.uploadAnnouncementImage(id, imageFile);
            }
            if (imageDeleted) {
              return this.service.deleteAnnouncementImage(id);
            }
            return of(null);
          }),
          map(() => AdminAnnouncementActions.saveSuccess()),
          catchError((error) => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map((mappedError) => {
              this.logger.error('Fehler beim Aktualisieren des Announcements.', mappedError);
              return AdminAnnouncementActions.saveFailure({ error: mappedError });
            }),
          )),
        );
      }),
    ),
  );

  /**
   * Aktualisiert ein bestehendes Announcement und veröffentlicht es anschließend.
   * Handhabt optional Bild-Upload oder Bild-Löschung vor dem Veröffentlichen.
   */
  readonly updateAndPublish$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAnnouncementActions.updateAndPublish),
      exhaustMap(({ id, dto }) => {
        const { imageFile, imageDeleted } = this.imageStagingService.consume();
        return this.service.updateAnnouncement(id, dto).pipe(
          switchMap(() => {
            if (imageFile) {
              return this.service.uploadAnnouncementImage(id, imageFile);
            }
            if (imageDeleted) {
              return this.service.deleteAnnouncementImage(id);
            }
            return of(null);
          }),
          switchMap(() => this.service.publishAnnouncement(id)),
          map(() => AdminAnnouncementActions.saveSuccess()),
          catchError((error) => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map((mappedError) => {
              this.logger.error('Fehler beim Aktualisieren und Veröffentlichen.', mappedError);
              return AdminAnnouncementActions.saveFailure({ error: mappedError });
            }),
          )),
        );
      }),
    ),
  );

  /**
   * Öffnet das Info-Popup als Vorschau für ein bestehendes Announcement.
   * Lädt die Detail-Daten und baut daraus die Vorschau-Daten.
   */
  readonly previewAnnouncement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAnnouncementActions.previewAnnouncement),
      switchMap(({ id }) =>
        this.service.readAnnouncement(id).pipe(
          switchMap((detail) => {
            const buildPreviewData = (imageUrl: string | null): AnnouncementPreviewData => ({
              title: detail.title,
              content: detail.content,
              imageUrl,
              linkLabel: detail.links?.[0]?.label ?? '',
              linkUrl: detail.links?.[0]?.url ?? '',
            });

            if (detail.imageId) {
              return this.announcementService.readAnnouncementImage(detail.imageId).pipe(
                map((blob) => InfoPopupDialogActions.openPreview({
                  previewData: buildPreviewData(URL.createObjectURL(blob)),
                })),
                catchError(() => of(InfoPopupDialogActions.openPreview({
                  previewData: buildPreviewData(null),
                }))),
              );
            }

            return of(InfoPopupDialogActions.openPreview({
              previewData: buildPreviewData(null),
            }));
          }),
          catchError((error) => of(error).pipe(
            mappedHttpErrorResponseOperator<ProblemDTO>(error),
            map((mappedError) => {
              this.logger.error('Fehler beim Laden der Vorschau-Daten.', mappedError);
              return AdminAnnouncementActions.saveFailure({ error: mappedError });
            }),
          )),
        ),
      ),
    ),
  );

  /**
   * Zeigt eine Erfolgsmeldung nach dem Speichern an.
   */
  readonly saveSuccessNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAnnouncementActions.saveSuccess),
      tap(() => this.snackBar.open(SNACKBAR_MESSAGES.saveSuccess, undefined, { duration: 3000 })),
    ), { dispatch: false },
  );

  readonly saveFailureNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAnnouncementActions.saveFailure),
      tap(({ error }) => this.snackBar.open(getErrorMessage(error, 'save'), undefined, { duration: 5000 })),
    ), { dispatch: false },
  );

  readonly publishSuccessNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnnouncementEntitiesActions.publishSuccess),
      tap(() => this.snackBar.open(SNACKBAR_MESSAGES.publishSuccess, undefined, { duration: 3000 })),
    ), { dispatch: false },
  );

  readonly publishFailureNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnnouncementEntitiesActions.publishFailure),
      tap(({ error }) => this.snackBar.open(getErrorMessage(error, 'publish'), undefined, { duration: 5000 })),
    ), { dispatch: false },
  );

  readonly unpublishSuccessNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnnouncementEntitiesActions.unpublishSuccess),
      tap(() => this.snackBar.open(SNACKBAR_MESSAGES.unpublishSuccess, undefined, { duration: 3000 })),
    ), { dispatch: false },
  );

  readonly unpublishFailureNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnnouncementEntitiesActions.unpublishFailure),
      tap(({ error }) => this.snackBar.open(getErrorMessage(error, 'unpublish'), undefined, { duration: 5000 })),
    ), { dispatch: false },
  );

  readonly deleteSuccessNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnnouncementEntitiesActions.deleteSuccess),
      tap(() => this.snackBar.open(SNACKBAR_MESSAGES.deleteSuccess, undefined, { duration: 3000 })),
    ), { dispatch: false },
  );

  readonly deleteFailureNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnnouncementEntitiesActions.deleteFailure),
      tap(({ error }) => this.snackBar.open(getErrorMessage(error, 'delete'), undefined, { duration: 5000 })),
    ), { dispatch: false },
  );

  /** Zeigt Snackbar bei Lade-Fehlern (Liste/Statistiken). Dedupliziert bei gleichzeitigen Fehlern. */
  readonly loadFailureNotification$ = createEffect(() =>
    merge(
      this.actions$.pipe(
        ofType(AnnouncementEntitiesActions.loadListFailure),
        map(({ error }) => getErrorMessage(error, 'loadList')),
      ),
      this.actions$.pipe(
        ofType(AnnouncementEntitiesActions.loadStatisticsFailure),
        map(({ error }) => getErrorMessage(error, 'loadStatistics')),
      ),
    ).pipe(
      debounceTime(100),
      tap((message) => this.snackBar.open(message, undefined, { duration: 5000 })),
    ), { dispatch: false },
  );

  readonly loadDetailFailureNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnnouncementDetailEntitiesActions.loadDetailFailure),
      tap(({ error }) => this.snackBar.open(getErrorMessage(error, 'loadDetail'), undefined, { duration: 5000 })),
    ), { dispatch: false },
  );

  /** Schließt den Editor und navigiert zurück zum Dashboard, wenn das Laden der Details fehlschlägt. */
  readonly loadDetailFailureCloseEditor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnnouncementDetailEntitiesActions.loadDetailFailure),
      map(() => AdminAnnouncementActions.closeEditor()),
    ),
  );

  /**
   * Schließt den Editor nach erfolgreichem Speichern.
   */
  readonly saveSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminAnnouncementActions.saveSuccess),
      map(() => AdminAnnouncementActions.closeEditor()),
    ),
  );

  /**
   * Schließt den Editor nach erfolgreichem Zurückrufen (Unpublish).
   */
  readonly unpublishCloseEditor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnnouncementEntitiesActions.unpublishSuccess),
      map(() => AdminAnnouncementActions.closeEditor()),
    ),
  );
}
